import { useSyncExternalStore, type ComponentType } from "react";
import { Link } from "react-router-dom";
import { Wallet, WalletCards, Clock, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { UNKNOWN_VALUE_LABEL } from "@/features/valuation/valuation";
import { usdCompact } from "../dashboard-selectors";

function useIsClient() {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );
}

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

type GreetingUser = { name?: string | null; email?: string | null } | null | undefined;

/** Neutral labels the app synthesizes for a session with no real name. */
const PLACEHOLDER_NAMES = new Set(["user", "operator", "guest"]);

/**
 * ALL-5825 (UX audit D13): a greeting must never call a person by a machine
 * handle. The session's `name` is sometimes a persona code or an id (`E2e`),
 * which rendered as "E2e!"; when it was absent the hero greeted the literal
 * "Operator". Only a word that reads as a given name is shown — otherwise the
 * greeting stands alone.
 *
 * A word is rejected when it contains a digit, has no vowel, is a neutral
 * placeholder label, or is the local part of the sign-in email. The check is
 * display-only: the session's data is never changed, and a real name is still
 * shown first-word-only.
 */
export function greetingName(user: GreetingUser): string | null {
  const raw = user?.name?.trim();
  if (!raw) return null;
  // `resolveUserIdentity` (src/lib/user-display.ts) stands in the neutral label
  // "User" when the session carries no usable name. It is a placeholder, not the
  // person, so it is treated the same as a missing name.
  if (PLACEHOLDER_NAMES.has(raw.toLowerCase())) return null;
  const first = raw.split(/\s+/)[0];
  if (!first) return null;
  if (/\d/.test(first)) return null;
  if (!/[aeiou]/i.test(first)) return null;
  const localPart = user?.email?.split("@")[0]?.trim().toLowerCase();
  if (localPart && first.toLowerCase() === localPart) return null;
  return first;
}

function todayLabel() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Token pairs, not stock palette colours (F26): the tiles used to hard-code
 * `bg-blue-50`/`text-blue-600` and friends, which do not move with the Alloy
 * tokens. `transfers` used indigo, which has no dedicated token, so it takes
 * the closest existing one (`info` — the same blue family the stock indigo
 * read as).
 */
const KPI_TONE: Record<string, { iconBg: string; iconColor: string }> = {
  aum: { iconBg: "bg-primary-soft", iconColor: "text-primary" },
  wallets: { iconBg: "bg-success-soft", iconColor: "text-success" },
  approvals: { iconBg: "bg-warning-soft", iconColor: "text-warning" },
  transfers: { iconBg: "bg-info-soft", iconColor: "text-info" },
};

function DecorativeHeroArt() {
  return (
    <svg
      viewBox="0 0 360 260"
      className="pointer-events-none absolute -right-4 top-0 h-full w-auto opacity-70"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <linearGradient id="hero-grad-1" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-accent)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--ads-viz-blue-soft)" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="hero-grad-2" x1="1" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-viz-violet)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--ads-viz-violet-soft)" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="hero-grad-3" x1="0.5" x2="0.5" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-viz-teal)" stopOpacity="0.14" />
          <stop offset="100%" stopColor="var(--ads-viz-teal-soft)" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <path d="M280 20 C340 60 360 140 320 200 C280 260 200 250 160 210 C120 170 140 100 200 70 C240 50 260 10 280 20Z" fill="url(#hero-grad-1)" />
      <path d="M220 80 C270 100 300 160 270 210 C240 260 170 250 150 200 C130 150 160 100 220 80Z" fill="url(#hero-grad-2)" />
      <path d="M180 150 C230 170 260 220 220 250 C180 280 120 260 110 220 C100 180 130 150 180 150Z" fill="url(#hero-grad-3)" />
    </svg>
  );
}

export interface HeroKpi {
  key: "aum" | "wallets" | "approvals" | "transfers";
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string | null;
  sub: string;
  href: string;
  loading: boolean;
  error: string | null;
}

/**
 * Each KPI card renders its own loading/error state instead of gating the
 * whole hero on one combined fetch (ALL-4747 T7): AUM/Active Wallets come
 * from `useWallets`, Pending Approvals from `useApprovals`, Transfers Today
 * from `useTransfers` — if one call fails the other three still render.
 */
export function HeroSection({ kpis }: { kpis: HeroKpi[] }) {
  const user = useAuthStore((s) => s.user);
  const isClient = useIsClient();

  // ALL-5825 (UX audit D13): the greeting is one line. A session with a real
  // given name reads "Good morning, Ana"; a session whose name is a persona
  // code, an id, or empty gets the greeting on its own — never a machine
  // handle and never the literal "Operator" (see `greetingName`).
  const name = greetingName(user);
  const salutation = isClient ? greetingForHour(new Date().getHours()) : "Welcome back";
  const greeting = name ? `${salutation}, ${name}` : salutation;
  const date = isClient ? todayLabel() : "—";

  return (
    <section className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/40 p-5 md:p-6">
      <DecorativeHeroArt />

      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-ink md:text-4xl">{greeting}</h1>
            <p className="mt-1 text-sm text-muted">
              Here&apos;s what&apos;s happening with your digital asset operations today.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-xs font-medium text-muted" suppressHydrationWarning>{date}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {kpis.map((kpi) => {
            const tone = KPI_TONE[kpi.key];
            const Icon = kpi.icon;
            const body = kpi.loading ? (
              <div className="mt-1 h-6 w-16 animate-pulse rounded bg-disabled-soft" aria-label={`Loading ${kpi.label}`} />
            ) : kpi.error ? (
              <div className="flex items-center gap-1 text-sm font-semibold text-danger" role="alert">
                <AlertTriangle className="size-3.5" aria-hidden />
                Unavailable
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {/* `value` is `string | null`: a tile that finished loading with no
                    figure renders the shared unknown dash, never an empty cell
                    (J4 / ALL-5638 — unknown and zero are different facts). */}
                <span className="text-xl font-bold tracking-tight text-ink md:text-2xl">
                  {kpi.value ?? UNKNOWN_VALUE_LABEL}
                </span>
              </div>
            );

            return (
              <Link
                key={kpi.key}
                to={kpi.href}
                className="group relative flex items-center gap-3 rounded-xl border border-line bg-surface p-3.5 transition-all hover:border-primary/40 md:p-4"
              >
                <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", tone.iconBg)}>
                  <Icon className={cn("size-5", tone.iconColor)} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-muted">{kpi.label}</div>
                  {body}
                  {/* The tile is a Link, so its hint must name what the click
                      actually does — open the list page, which re-requests on
                      mount. "click to retry" promised an in-place retry the
                      tile has no handler for (F18 / ALL-4839). */}
                  <div className="text-xs text-muted">{kpi.error ? "Couldn't load — open to retry" : kpi.sub}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export const HERO_KPI_ICONS = { Wallet, WalletCards, Clock, ArrowRightLeft } as const;
export { usdCompact };
